/**
 * /dashboard/accounting/sales — Sales hub (G_ACC5).
 *
 * Tabs:
 *   Invoices — list with status / customer / date filters
 *   Quotes   — list with status / customer filters
 *
 * "New invoice" / "New quote" CTAs route to the per-tab create page.
 * Tab state is mirrored to the URL (?tab=quotes) so a deep link from
 * an email / a dashboard quick-link lands on the right tab without
 * forcing the operator to click twice.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/sales/page.tsx.
 *   - "use client" dropped
 *   - useRouter / useSearchParams → Route.useSearch() + useNavigate
 *   - URL state validated by zod (matches the per-route schema pattern
 *     established in dashboard.approvals.tsx).
 */

import { useCallback } from "react";
import { createFileRoute, Link, useNavigate , useParams} from "@tanstack/react-router";
import { z } from "zod";
import { Plus } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { InvoiceList } from "@/components/accounting/invoice-list";
import { QuoteList } from "@/components/accounting/quote-list";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type SalesTab = "invoices" | "quotes";

const salesSearchSchema = z.object({
  tab: z.enum(["invoices", "quotes"]).optional(),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/sales/")({
  validateSearch: salesSearchSchema,
  component: SalesHubRoute,
});

function isSalesTab(v: string): v is SalesTab {
  return v === "invoices" || v === "quotes";
}

function SalesHubRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const search = Route.useSearch();
  const navigate = useNavigate();
  const tab: SalesTab = search.tab ?? "invoices";

  const setTab = useCallback(
    (value: string) => {
      const next: SalesTab = isSalesTab(value) ? value : "invoices";
      void navigate({
        to: "/app/$tenantSlug/accounting/sales", params: { tenantSlug },
        search: { tab: next },
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <AccountingModuleShell
      title="Sales"
      description="Invoices and quotes for your customers."
      actions={
        <>
          {tab === "invoices" && (
            <Button asChild className="gap-1.5">
              <Link to="/app/$tenantSlug/accounting/sales/invoices/new" params={{ tenantSlug }}>
                <Plus aria-hidden className="h-4 w-4" />
                New invoice
              </Link>
            </Button>
          )}
          {tab === "quotes" && (
            <Button asChild className="gap-1.5">
              <Link to="/app/$tenantSlug/accounting/sales/quotes/new" params={{ tenantSlug }}>
                <Plus aria-hidden className="h-4 w-4" />
                New quote
              </Link>
            </Button>
          )}
        </>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="quotes">Quotes</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-4">
          <InvoiceList />
        </TabsContent>
        <TabsContent value="quotes" className="mt-4">
          <QuoteList />
        </TabsContent>
      </Tabs>
    </AccountingModuleShell>
  );
}
